// Tiny Web Audio arcade engine for Seat Fighter — synth music loop + per-power SFX.
let ctx = null;
let musicGain = null;
let musicTimer = null;
let muted = false;

const ensure = () => {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = new AC();
    musicGain = ctx.createGain();
    musicGain.gain.value = muted ? 0 : 0.28;
    musicGain.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
};

const note = (freq, t, dur, type = 'square', gain = 0.2, dest = null) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.value = freq;
  o.connect(g);
  g.connect(dest || ctx.destination);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(gain, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
  o.start(t);
  o.stop(t + dur + 0.03);
};

const sweep = (f0, f1, t, dur, type = 'square', gain = 0.25, dest = null) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, t);
  o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
  o.connect(g);
  g.connect(dest || ctx.destination);
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
  o.start(t);
  o.stop(t + dur + 0.03);
};

const noise = (t, dur, gain = 0.3, freq = 2000, dest = null) => {
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.value = freq;
  const g = ctx.createGain();
  src.connect(f);
  f.connect(g);
  g.connect(dest || ctx.destination);
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
  src.start(t);
  src.stop(t + dur);
};

// looping arcade bed — per-duel themes, 16-step lookahead-scheduled
const THEMES = {
  'dipke-vs-modi': { tempo: 142, bt: 'triangle', at: 'square',
    bass: [110, 0, 110, 55, 146.83, 0, 110, 0, 130.81, 0, 130.81, 65.41, 98, 0, 98, 123.47],
    arp: [440, 523.25, 659.25, 523.25, 587.33, 659.25, 880, 659.25, 523.25, 659.25, 783.99, 659.25, 493.88, 587.33, 739.99, 587.33] },
  'dhruv-vs-gyanu': { tempo: 126, bt: 'square', at: 'triangle',
    bass: [65.41, 0, 65.41, 0, 77.78, 0, 65.41, 0, 87.31, 0, 87.31, 0, 58.27, 0, 65.41, 0],
    arp: [523.25, 622.25, 783.99, 622.25, 698.46, 622.25, 523.25, 466.16, 523.25, 622.25, 783.99, 932.33, 698.46, 622.25, 523.25, 466.16] },
  'reporters-vs-raf': { tempo: 152, bt: 'sawtooth', at: 'square',
    bass: [82.41, 0, 82.41, 41.2, 110, 0, 82.41, 0, 98, 0, 98, 49, 73.42, 0, 82.41, 0],
    arp: [659.25, 784, 988, 784, 880, 784, 659.25, 587.33, 659.25, 784, 988, 1047, 880, 784, 659.25, 587.33] },
  'musk-vs-ambani': { tempo: 132, bt: 'sine', at: 'sine',
    bass: [73.42, 0, 0, 73.42, 98, 0, 0, 98, 110, 0, 0, 110, 65.41, 0, 0, 65.41],
    arp: [440, 587.33, 880, 587.33, 659.25, 880, 1174.66, 880, 440, 587.33, 880, 659.25, 587.33, 880, 659.25, 440] },
  'budget-vs-middleclass': { tempo: 118, bt: 'triangle', at: 'square',
    bass: [98, 0, 98, 0, 130.81, 0, 98, 0, 110, 0, 110, 0, 87.31, 0, 98, 0],
    arp: [587.33, 698.46, 880, 698.46, 784, 880, 1046.5, 880, 587.33, 698.46, 880, 784, 698.46, 587.33, 523.25, 587.33] },
};
let theme = THEMES['dipke-vs-modi'];
let SEC16 = 60 / theme.tempo / 4;
let nextNoteTime = 0;
let cur16 = 0;

export const setMusicTheme = (slug) => {
  theme = THEMES[slug] || THEMES['dipke-vs-modi'];
  SEC16 = 60 / theme.tempo / 4;
};

const kick = (t) => sweep(150, 46, t, 0.14, 'sine', 0.55, musicGain);
const hat = (t, g) => noise(t, 0.028, g, 9000, musicGain);

const scheduleStep = (s, t) => {
  const b = theme.bass[s];
  if (b) note(b, t, SEC16 * 2.1, theme.bt, 0.42, musicGain);
  note(theme.arp[s], t, SEC16 * 0.85, theme.at, 0.1, musicGain);
  if (s % 2 === 0) kick(t);
  hat(t, s % 2 ? 0.13 : 0.07);
};

const scheduler = () => {
  if (!ctx) return;
  while (nextNoteTime < ctx.currentTime + 0.12) {
    scheduleStep(cur16 % 16, nextNoteTime);
    nextNoteTime += SEC16;
    cur16++;
  }
};

export const initAudio = () => {
  ensure();
  // iOS Safari: play one inaudible buffer inside the user gesture so the
  // context stays running even though real scheduling happens after an await.
  try {
    const b = ctx.createBuffer(1, 1, ctx.sampleRate);
    const s = ctx.createBufferSource();
    s.buffer = b;
    s.connect(ctx.destination);
    s.start(0);
  } catch (e) { /* noop */ }
  return ctx;
};

export const startMusic = () => {
  ensure();
  if (musicTimer) return;
  cur16 = 0;
  nextNoteTime = ctx.currentTime + 0.06;
  musicTimer = setInterval(scheduler, 25);
};

export const stopMusic = () => {
  if (musicTimer) {
    clearInterval(musicTimer);
    musicTimer = null;
  }
};

export const setMuted = (m) => {
  muted = m;
  if (musicGain) musicGain.gain.value = m ? 0 : 0.28;
};

export const playSfx = (kind) => {
  if (muted || !ctx) return;
  ensure();
  const t = ctx.currentTime + 0.01;
  switch (kind) {
    case 'swarm':
      for (let i = 0; i < 7; i++) noise(t + i * 0.035, 0.06, 0.16, 1400);
      break;
    case 'uppercut':
      sweep(180, 1100, t, 0.42, 'square', 0.28);
      break;
    case 'papers':
      noise(t, 0.26, 0.16, 3200);
      note(520, t + 0.05, 0.2, 'triangle', 0.14);
      break;
    case 'chestplate':
      sweep(150, 40, t, 0.5, 'sine', 0.5);
      noise(t, 0.12, 0.28, 420);
      break;
    case 'soundwave': {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      const lfo = ctx.createOscillator();
      const lg = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 330;
      lfo.frequency.value = 13;
      lg.gain.value = 130;
      lfo.connect(lg);
      lg.connect(o.frequency);
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.24, t);
      g.gain.exponentialRampToValueAtTime(0.0008, t + 0.5);
      o.start(t);
      lfo.start(t);
      o.stop(t + 0.5);
      lfo.stop(t + 0.5);
      break;
    }
    case 'hologram':
      for (let i = 0; i < 6; i++) note(620 + Math.random() * 760, t + i * 0.05, 0.08, 'square', 0.11);
      break;
    case 'hit':
      noise(t, 0.1, 0.34, 800);
      sweep(130, 55, t, 0.12, 'square', 0.3);
      break;
    case 'ko':
      sweep(420, 70, t, 0.75, 'sawtooth', 0.4);
      noise(t, 0.5, 0.28, 1600);
      break;
    default:
      noise(t, 0.1, 0.3, 900);
  }
};
